import { Fragment, ReactNode } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { cn } from '@lib/utils/cn';

export interface DropdownMenuItem {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  divider?: boolean;
}

interface DropdownMenuProps {
  trigger: ReactNode;
  items: DropdownMenuItem[];
  align?: 'left' | 'right';
  className?: string;
}

const variantClasses = {
  default: 'text-secondary-700 dark:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-700',
  danger: 'text-danger-700 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20',
  success: 'text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20',
  warning: 'text-warning-700 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20',
};

export function DropdownMenu({ trigger, items, align = 'right', className }: DropdownMenuProps) {
  return (
    <Menu as="div" className={cn('relative inline-block text-left', className)}>
      <Menu.Button as="div" className="cursor-pointer">
        {trigger}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          className={cn(
            'absolute z-50 mt-2 w-56 origin-top-right rounded-md',
            'bg-white dark:bg-secondary-800 shadow-lg ring-1 ring-black ring-opacity-5',
            'focus:outline-none',
            align === 'left' ? 'left-0' : 'right-0'
          )}
        >
          <div className="py-1">
            {items.map((item, index) => (
              <Fragment key={index}>
                {item.divider ? (
                  <div className="my-1 border-t border-secondary-200 dark:border-secondary-700" />
                ) : (
                  <Menu.Item disabled={item.disabled}>
                    {({ active }) => (
                      <button
                        onClick={item.onClick}
                        disabled={item.disabled}
                        className={cn(
                          'group flex w-full items-center px-4 py-2 text-sm transition-colors',
                          item.disabled
                            ? 'cursor-not-allowed opacity-50'
                            : active
                            ? variantClasses[item.variant || 'default']
                            : 'text-secondary-700 dark:text-secondary-200'
                        )}
                      >
                        {item.icon && (
                          <item.icon
                            className={cn(
                              'mr-3 h-5 w-5',
                              item.disabled ? 'opacity-50' : ''
                            )}
                            aria-hidden="true"
                          />
                        )}
                        {item.label}
                      </button>
                    )}
                  </Menu.Item>
                )}
              </Fragment>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
